import React, { useState } from "react";
import { Box, Typography, Collapse } from "@mui/material";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import useSettingsSelector from "../../store/selectors/settings";
import { useWidgetMsg } from "../../hooks/widget/useWidgetMsg";

const Message = ({ content, sx = {}, isAttr = false, conversationId, handleNavigate, setSelectedDocument }) => {
	const { RECEIVER } = useSettingsSelector();
	const { msg } = useWidgetMsg();
	const [sourcesExpanded, setSourcesExpanded] = useState(false);
	const rawMessage = content?._streamText ?? content?.message ?? content?.config?.message ?? "";
	const isStreaming = content?.streaming === true && typeof content?._streamText === "string";
	const cursor = "\u258D";
	const message = isStreaming ? rawMessage.replace(/(\n?)$/, "$1" + cursor) : rawMessage;

	const resolveSources = (content) => {
		if (!content) return [];
		if (Array.isArray(content.sourceDocuments)) {
			return content.sourceDocuments;
		}
		if (Array.isArray(content.config?.sourceDocuments)) {
			return content.config.sourceDocuments;
		}
		if (typeof content.fullMessage === "string") {
			try {
				const parsed = JSON.parse(content.fullMessage);
				if (Array.isArray(parsed.source_documents)) {
					return parsed.source_documents;
				}
			} catch (e) {
				console.warn("Invalid fullMessage JSON", e);
			}
		}
		return [];
	};

	const sourceDocuments = resolveSources(content);

	// Custom link renderer with class
	const renderLink = ({ href, children, ...props }) => {
		return (
			<a
				href={href}
				target="_blank"
				rel="noopener noreferrer"
				className="markdown-link"
				{...props}
			>
				{children}
			</a>
		);
	};

	const filteredSources = Array.isArray(sourceDocuments)
		? sourceDocuments.filter(src => {
			const { name = "", url = "" } = src;
			const isSame = name === url;
			const isFileExt = /\.(pdf|docx|csv)$/i.test(url);
			return !isSame && !isFileExt;
		})
		: [];

	const hasSources = filteredSources.length > 0;

	return (
		<Box
			className="markdown-message-box"
			sx={{
				margin: 0,
				backgroundColor: RECEIVER.BACKGROUND_COLOR,
				padding: "16px",
				borderTopRightRadius: "12px",
				borderBottomRightRadius: "12px",
				borderBottomLeftRadius: "12px",
				overflowWrap: "break-word",
				wordBreak: "break-word",
				width: isAttr ? "fit-content" : "auto",
				...sx,
			}}
		>
			<ReactMarkdown
				remarkPlugins={[remarkGfm]}
				components={{
					a: renderLink,
					img: ({ src, alt }) => (
						<img
							src={src}
							alt={alt}
							style={{
								maxWidth: "100%",
								height: "auto",
								borderRadius: "8px",
								marginTop: "12px",
								display: "block",
							}}
						/>
					),
				}}
			>
				{String(message || "")}
			</ReactMarkdown>
			{hasSources && (
				<Box className="sources-wrapper">
					<Box className="sources-divider" />
					<Box
						onClick={() => setSourcesExpanded((prev) => !prev)}
						sx={{
							cursor: "pointer",
							userSelect: "none",
							display: "inline-flex",
							alignItems: "center",
							gap: "4px",
							px: "8px",
							py: "3px",
							borderRadius: "8px",
							border: "1px solid rgba(0,0,0,0.12)",
							backgroundColor: "rgba(0,0,0,0.04)",
						}}
					>
						<MenuBookOutlinedIcon sx={{ fontSize: 15, opacity: 0.7 }} />
						<Typography variant="body2" className="source-label" sx={{ m: 0 }}>
							{filteredSources.length}{" "}
							{filteredSources.length === 1
								? msg("conversations.source", "Source")
								: msg("conversations.sources", "Sources")}
						</Typography>
						{sourcesExpanded
							? <ExpandLessIcon sx={{ fontSize: 15, opacity: 0.6 }} />
							: <ExpandMoreIcon sx={{ fontSize: 15, opacity: 0.6 }} />}
					</Box>
					<Collapse in={sourcesExpanded}>
						{filteredSources.map((src, idx) => {
							const { name, url } = src;
							return (
								<Box
									key={idx}
									className="source-link"
									title={name}
									onClick={() => {
										if (handleNavigate && setSelectedDocument) {
											setSelectedDocument({ name, url, title: name });
											handleNavigate({ location: "document", conversationId, assignedAgents: undefined });
										}
									}}
								>
									{name}
								</Box>
							);
						})}
					</Collapse>
				</Box>
			)}
		</Box>
	);
};

export default Message;
